import type { PlayerVipLevel } from '~/types/player-profile';

// Normalização do nível VIP que o core manda em `/players/me`.
//
// Vive aqui, e não no mapper do adapter, porque DOIS lugares recebem esse valor
// de fora e nenhum dos dois pode confiar no que chega: a resposta do core (wire)
// e o memo desta aba (sessionStorage, que o próprio jogador consegue editar pelo
// devtools). Como o nível escolhe a arte do card, um valor forjado ali viraria
// badge de VIP 6 — então quem lê revalida, sempre, pelo mesmo caminho.
//
// DUAS FORMAS DE FIO, de propósito:
//
//   'VIP 3'                                    ← contrato ATUAL (19/08/2026)
//   { tag: 'VIP 3', order: 3, color: '#...' }  ← contrato anterior
//
// O core achatou o `vip` de objeto pra string (o que era `vip.tag` virou o
// `vip` inteiro), e as duas são aceitas aqui não por indecisão: enquanto a
// mudança não estiver em todos os ambientes, front novo conversa com core
// antigo e vice-versa; e o sessionStorage de uma aba já aberta guarda o formato
// de ANTES do deploy do front. Uma das duas leituras falhar significa jogador
// VIP lido como comum — o sintoma que trouxe este arquivo aqui.
//
// Sem nível não há `vip`: string vazia, objeto sem `tag` e `null` explícito são
// todos o mesmo caminho, o do jogador comum.

// O tier a partir do TEXTO da tag.
//
// Isto é uma regressão consciente, e vale registrar por quê: o tier JÁ tinha
// saído do texto uma vez (ver useVipStatus), porque o formato da tag não é
// contrato versionado por ninguém — foi assim que o parser antigo, que esperava
// `VIP_3`, quebrou quando o core escreveu 'VIP 3'. Voltamos ao texto porque o
// core parou de mandar o `order`, e não porque o texto ficou confiável: é a
// única fonte de nível que sobrou no fio.
//
// Daí a leitura ser folgada de propósito — separador livre entre 'VIP' e o
// número, caixa e espaço das pontas ignorados. O que ela NÃO faz é adivinhar:
// texto que não casa devolve `null`, que cai no card padrão. Um default
// numérico aqui promoveria jogador comum a VIP.
export function vipOrderFromTag(tag: string): number | null {
  const normalized = tag.trim().toUpperCase();

  // O nível mais alto da casa não tem número na tag — o `order` 7 dele é
  // convenção do core (VipLevelSeeder), não algo que dê pra ler do texto.
  if (normalized === 'SIGNATURE') return 7;

  const match = /^VIP[\s_-]*(\d+)$/.exec(normalized);

  if (!match) return null;

  const order = Number(match[1]);

  // `Number` de um `\d+` não tem como dar NaN; o que se barra aqui é nível fora
  // da escada do core (0..7). Um 'VIP 42' inventado cai no card padrão em vez de
  // virar índice que a tabela de artes não tem.
  return order >= 0 && order <= 7 ? order : null;
}

// `supportWhatsapp` mudou de lugar junto com o achatamento do `vip`: contrato
// atual manda `support_whatsapp` solto na RAIZ da resposta de /players/me,
// irmão de `vip` (não mais aninhado dentro dele) — por isso entra como
// segundo parâmetro aqui, não como parte do `value` do `vip`. Contrato
// anterior (objeto) ainda é aceito como fallback, e o cache desta aba
// (sessionStorage) também bate nesse caminho ao reler o que ele mesmo já
// normalizou — daí aceitar tanto `support_whatsapp` (fio antigo) quanto
// `supportWhatsapp` (o que este arquivo já devolveu numa leitura anterior).
export function normalizePlayerVip(
  value: unknown,
  supportWhatsapp?: unknown,
): PlayerVipLevel | null {
  const rootSupportWhatsapp =
    typeof supportWhatsapp === 'string' ? supportWhatsapp : null;

  // Contrato atual: o `vip` É a tag.
  if (typeof value === 'string') {
    const tag = value.trim();

    if (!tag) return null;

    return {
      tag,
      order: vipOrderFromTag(tag),
      // O core não manda mais a cor do backoffice. Nunca foi usada pra desenhar
      // (a arte dos cards vem do Figma e as duas paletas divergem de propósito),
      // então o campo continua existindo só pro tipo não mudar de forma pra quem
      // lê — e permanece `null` neste caminho.
      color: null,
      supportWhatsapp: rootSupportWhatsapp,
    };
  }

  // Contrato anterior, ainda aceito: aqui o `order` vem do core e é MELHOR que o
  // derivado do texto, então é ele que manda quando existe.
  if (!value || typeof value !== 'object') return null;

  const raw = value as Record<string, unknown>;

  if (typeof raw.tag !== 'string' || !raw.tag) return null;

  const nestedSupportWhatsapp = raw.support_whatsapp ?? raw.supportWhatsapp;

  return {
    tag: raw.tag,
    // `order` é o NÍVEL (ver PlayerVipLevel), então não tem default numérico:
    // 0 é um nível de verdade (VIP 0, com arte própria) e cair nele por payload
    // quebrado promoveria um jogador comum a VIP. Ausente ⇒ cai no texto da tag,
    // o mesmo caminho do contrato novo ⇒ `null` ⇒ arte padrão.
    order: typeof raw.order === 'number' ? raw.order : vipOrderFromTag(raw.tag),
    color: typeof raw.color === 'string' ? raw.color : null,
    supportWhatsapp:
      rootSupportWhatsapp ??
      (typeof nestedSupportWhatsapp === 'string'
        ? nestedSupportWhatsapp
        : null),
  };
}
